import jsonpatch from "fast-json-patch";

const { applyPatch, deepClone } = jsonpatch;

import type {
  AtomData,
  ChangeLog,
  ChangelLogMetaInfo,
  VersionedAtomData,
} from "../VersionAtom";
import { applyDefaultsForMissingKeys } from "./normalizeVersionedAtomPayload";

/**
 * Versioned atoms have the latest value included now.
 * Still used when computing snapshot state from __history (history already
 * filtered by cutoff in the provide layer).
 *
 * @param defaults - Optional JSDoc `@default` map for the data interface; used to
 *   back-fill fields missing from older stored patch history.
 */
export function calculateLatestVersionedAtomValue<
  DataType extends AtomData = AtomData,
>(
  history: Array<ChangeLog<DataType>>,
  defaults?: Record<string, unknown>,
): DataType & ChangelLogMetaInfo {
  if (history.length === 0) {
    const empty = {} as DataType & ChangelLogMetaInfo;
    return defaults
      ? (applyDefaultsForMissingKeys(
          empty as unknown as Record<string, unknown>,
          defaults,
        ) as DataType & ChangelLogMetaInfo)
      : empty;
  }

  const latest = history.reduce(
    (acc, { changes }) => {
      return applyPatch(acc, deepClone(changes)).newDocument;
    },
    {} as DataType & ChangelLogMetaInfo,
  );

  const latestHistoryElement = history.at(-1);
  if (!latestHistoryElement) {
    const empty = {} as DataType & ChangelLogMetaInfo;
    return defaults
      ? (applyDefaultsForMissingKeys(
          empty as unknown as Record<string, unknown>,
          defaults,
        ) as DataType & ChangelLogMetaInfo)
      : empty;
  }

  latest.date = latestHistoryElement.date;
  latest.author = latestHistoryElement.author;

  return defaults
    ? (applyDefaultsForMissingKeys(
        latest as unknown as Record<string, unknown>,
        defaults,
      ) as DataType & ChangelLogMetaInfo)
    : latest;
}

export function calculateVersionedAtomValueAtCutoff<
  DataType extends AtomData = AtomData,
>(
  value: VersionedAtomData<DataType>,
  cutoffDate: Date,
  defaults?: Record<string, unknown>,
): VersionedAtomData<DataType> {
  const cutoffMs = cutoffDate.getTime();
  if (Number.isNaN(cutoffMs)) {
    return value;
  }

  const history = Array.isArray(value.__history) ? value.__history : [];
  if (history.length === 0) {
    return value;
  }

  const filteredHistory = history.filter((entry) => {
    if (!entry?.date) return false;
    const entryMs = new Date(entry.date).getTime();
    if (Number.isNaN(entryMs)) return false;
    return entryMs <= cutoffMs;
  });

  let doc = {} as DataType;
  for (const entry of filteredHistory) {
    doc = applyPatch(doc, deepClone(entry.changes), false, true).newDocument;
  }

  if (defaults) {
    doc = applyDefaultsForMissingKeys(
      doc as unknown as Record<string, unknown>,
      defaults,
    ) as DataType;
  }

  const latestEntry = filteredHistory.at(-1);
  const latestMeta = latestEntry
    ? { author: latestEntry.author, date: latestEntry.date }
    : value.__latestMeta;

  return {
    ...doc,
    __latestMeta: latestMeta,
    __history: filteredHistory,
  } as VersionedAtomData<DataType>;
}
